import { Link } from 'react-router';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  ClipboardList,
  Database,
  KeyRound,
  LockKeyhole,
  RefreshCw,
  Search,
  ShieldCheck,
  Trophy,
} from 'lucide-react';
import Eyebrow from '../components/ui/Eyebrow';
import HeroBackground from '../components/ui/HeroBackground';
import SectionHeader from '../components/ui/SectionHeader';
import { BRUINS09_LEAGUE_SETTINGS } from '../config/leagueSettings';
import {
  canPublishKeeperVerdict,
  evidenceGradeForDossier,
  missingRequiredSourceTypes,
} from '../utils/keeperResearch';

const ICONS = {
  activity: Activity,
  chart: BarChart3,
  database: Database,
  key: KeyRound,
  shield: ShieldCheck,
  trophy: Trophy,
};

export default function KeeperResearchView({ dossier }) {
  if (!dossier || !dossier.meta) {
    return (
      <section className="px-6 md:px-14 py-20">
        <h1 className="font-display text-5xl font-black uppercase">No dossier found.</h1>
        <p className="mt-4 text-ink-secondary italic">The requested keeper dossier is not available.</p>
        <Link to="/keeper-vault" className="mt-6 inline-flex border border-accent-gold px-4 py-3 text-[11px] font-extrabold tracking-[0.22em] text-accent-gold uppercase">
          Back to vault
        </Link>
      </section>
    );
  }

  const {
    meta,
    summaryCards = [],
    decisionCards = [],
    boardLegend = null,
    riskRubric = [],
    candidates = [],
    scoringTranslation = [],
    sourceRequirements = [],
    evidencePack = [],
    weeklyRefresh = [],
    sourceLedger = [],
    changeLog = [],
    changeNotes = [],
    dataCaveats = [],
  } = dossier;

  const evidenceGrade = evidenceGradeForDossier({ ...meta, sourceLedger });
  const missingSourceCount = missingRequiredSourceTypes(sourceLedger).length;
  const publishable = canPublishKeeperVerdict({ ...meta, sourceLedger });
  const isDemo = Boolean(meta.isDemo);
  const headlineEyebrow = isDemo ? 'UNLOCKED RESEARCH PREVIEW' : `${(meta.manager || '').toUpperCase()} — KEEPER DOSSIER`;
  const headlineTitle = isDemo ? (
    <>
      What winning<br />
      <span className="text-accent-gold">unlocks.</span>
    </>
  ) : (
    <>
      {meta.manager}'s<br />
      <span className="text-accent-gold">single keeper.</span>
    </>
  );
  const heroLead = isDemo
    ? 'This is the test layout for the keeper research page a manager sees after clearing the gauntlet. The real version should be data-heavy, source-cited, and refreshed every week until the draft. The player examples below are demo data.'
    : `Current Aug. 19 research for ${meta.manager}'s ${meta.yahooTeamName || 'roster'}. The authenticated Yahoo final roster and original 2025 draft costs are joined to a dated 2026 superflex market, then adjusted for Bruins09 scoring.`;
  const bannerLabel = isDemo ? 'Sample only' : 'Current — provisional';
  const bannerCopy = isDemo
    ? 'Do not use this page to choose a keeper yet. We now have the Yahoo roster source and 2025 draft costs; final dossiers still require current ADP, projections, player news, source citations, and a weekly update log.'
    : 'Yahoo roster and draft-cost evidence plus the Aug. 19 market are live. The commissioner confirmed that players not drafted in 2025 cost no pick. The verdict remains provisional until the Aug. 25 injury and depth-chart pass is complete.';
  const dossierEyebrow = `${(meta.manager || 'SAMPLE').toUpperCase()} ${isDemo ? 'SAMPLE' : 'REAL'} DOSSIER`;
  const decisionBoardActionLabel = isDemo ? 'DEMO DATA' : 'REAL EVIDENCE';

  return (
    <>
      <HeroBackground variant="gold" className="px-6 md:px-14 py-14 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-8 items-end">
          <div>
            <Eyebrow>{headlineEyebrow}</Eyebrow>
            <h1
              className="mt-4 font-display font-black uppercase leading-[0.88] tracking-normal text-ink-primary"
              style={{ fontSize: 'clamp(48px, 7.4vw, 112px)' }}
            >
              {headlineTitle}
            </h1>
            <p className="mt-6 max-w-3xl text-base md:text-xl text-ink-secondary leading-relaxed italic">
              {heroLead}
            </p>
          </div>

          <article className="bg-bg-deep/90 border border-accent-gold p-5 md:p-6">
            <div className="flex items-center gap-3 text-accent-red">
              <AlertTriangle aria-hidden="true" className="w-5 h-5" />
              <div className="text-[10px] tracking-[0.28em] font-extrabold uppercase">
                {bannerLabel}
              </div>
            </div>
            <p className="mt-4 text-sm md:text-base text-ink-secondary leading-relaxed italic">
              {bannerCopy}
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <MiniStatus label="As of" value={meta.asOfLabel} />
              <MiniStatus label="Next refresh" value={meta.nextRefreshLabel} />
              <MiniStatus label="Evidence" value={evidenceGrade} />
              <MiniStatus label="Publishable" value={publishable ? 'Yes' : 'No'} tone={publishable ? 'gold' : 'red'} />
            </div>
            <Link
              to="/keeper-vault"
              className="mt-5 inline-flex items-center justify-center gap-2 border border-accent-gold px-4 py-3 text-[11px] font-extrabold tracking-[0.22em] text-accent-gold uppercase hover:bg-accent-gold hover:text-bg-deep transition-colors"
            >
              <LockKeyhole aria-hidden="true" className="w-4 h-4" />
              Back to vault
            </Link>
          </article>
        </div>
      </HeroBackground>

      <section className="px-6 md:px-14 py-12 border-y border-border bg-bg-deep">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {summaryCards.map((card) => {
            const Icon = ICONS[card.iconKey] || Search;
            return (
              <article key={card.label} className="bg-bg-surface border-t-[3px] border-accent-gold p-4 md:p-5">
                <Icon aria-hidden="true" className="w-5 h-5 text-accent-gold mb-4" />
                <div className="text-[9px] md:text-[10px] text-ink-tertiary tracking-[0.25em] font-extrabold uppercase">
                  {card.label}
                </div>
                <div className="mt-2 font-display text-2xl md:text-3xl font-black tracking-normal text-accent-gold uppercase">
                  {card.value}
                </div>
                <p className="mt-2 text-[12px] md:text-sm text-ink-secondary leading-snug italic">
                  {card.copy}
                </p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="px-6 md:px-14 py-14">
        <SectionHeader
          eyebrow={dossierEyebrow}
          title="Keeper Decision Board."
          actionLabel={decisionBoardActionLabel}
          actionHref="#candidate-board"
        />

        <div className="grid grid-cols-1 xl:grid-cols-[0.92fr_1.08fr] gap-6 md:gap-8">
          <article className="bg-bg-surface border-t-[3px] border-accent-gold p-6 md:p-8">
            <Eyebrow>EXECUTIVE READ</Eyebrow>
            <h2 className="mt-3 font-display text-4xl md:text-6xl font-black uppercase tracking-normal leading-none">
              {isDemo
                ? 'The page should make the decision easy to argue.'
                : `One player. One pick lost. Make it count.`}
            </h2>
            <p className="mt-5 text-sm md:text-base text-ink-secondary leading-relaxed italic">
              {isDemo
                ? 'The final version should not bury the answer. It should say the best keeper, why the price matters, which alternatives are close, and what new information could flip the recommendation before the deadline.'
                : 'Bruins09 lets each manager keep one player. A drafted player costs the round in which they were selected in 2025, even after a trade or waiver claim; a player who went undrafted costs no pick. The cards name the current leader, alternatives, risks, and the evidence still needed.'}
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Readout label="League" value={BRUINS09_LEAGUE_SETTINGS.name} />
              <Readout label="Roster edge" value="2QB, 3WR, W/R/T" />
              <Readout label="Scoring edge" value="Full PPR, 6-pt pass TD" />
              <Readout label="Deadline" value={meta.keeperDeadlineLabel || BRUINS09_LEAGUE_SETTINGS.keeperDeadlineLabel} />
            </div>
          </article>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {decisionCards.map((card) => (
              <article key={card.label} className="bg-bg-surface border border-border p-5">
                <div className="text-[10px] tracking-[0.25em] text-accent-gold font-extrabold uppercase">
                  {card.label}
                </div>
                <div className="mt-2 font-display text-3xl font-black uppercase tracking-normal leading-none">
                  {card.player}
                </div>
                <div className="mt-3 inline-flex bg-bg-deep px-2 py-1 text-[9px] tracking-[0.2em] text-ink-tertiary font-extrabold uppercase">
                  {card.tag}
                </div>
                <p className="mt-3 text-sm text-ink-secondary leading-relaxed italic">
                  {card.copy}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {(boardLegend || riskRubric.length > 0) && (
        <section className="px-6 md:px-14 py-14 border-t border-border">
          <SectionHeader
            eyebrow="HOW TO READ THE BOARD"
            title="Every number explains itself."
          />

          {boardLegend && (
            <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-6 md:gap-8">
              <article className="bg-bg-surface border-t-[3px] border-accent-gold p-6 md:p-8">
                <p className="text-base md:text-lg text-ink-secondary leading-relaxed italic">
                  {boardLegend.intro}
                </p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(boardLegend.columns || []).map((col) => (
                    <div key={col.label} className="bg-bg-deep border border-border p-4">
                      <div className="text-[10px] tracking-[0.25em] text-accent-gold font-extrabold uppercase">
                        Column
                      </div>
                      <div className="mt-2 font-display text-2xl font-black uppercase tracking-normal">
                        {col.label}
                      </div>
                      <p className="mt-2 text-sm text-ink-tertiary leading-relaxed italic">
                        {col.copy}
                      </p>
                    </div>
                  ))}
                </div>
              </article>

              <div className="space-y-4">
                {boardLegend.formula && (
                  <article className="bg-bg-surface border-l-[3px] border-accent-gold p-5">
                    <div className="text-[10px] tracking-[0.25em] text-accent-gold font-extrabold uppercase">
                      Surplus formula
                    </div>
                    <p className="mt-2 font-display text-2xl md:text-3xl font-black uppercase tracking-normal leading-tight text-accent-gold">
                      {boardLegend.formula}
                    </p>
                  </article>
                )}
                {boardLegend.example && (
                  <article className="bg-bg-surface border-l-[3px] border-accent-gold p-5">
                    <div className="text-[10px] tracking-[0.25em] text-accent-gold font-extrabold uppercase">
                      Worked example
                    </div>
                    <p className="mt-2 text-sm md:text-base text-ink-secondary leading-relaxed italic">
                      {boardLegend.example}
                    </p>
                  </article>
                )}
                {riskRubric.length > 0 && (
                  <article className="bg-bg-surface border-l-[3px] border-accent-red p-5">
                    <div className="text-[10px] tracking-[0.25em] text-accent-red font-extrabold uppercase">
                      Risk rubric
                    </div>
                    <div className="mt-3 space-y-3">
                      {riskRubric.map((rule) => (
                        <div key={rule.level} className="border-t border-border pt-3 first:border-t-0 first:pt-0">
                          <div className={`font-display text-xl font-black uppercase tracking-normal ${rule.color === 'red' ? 'text-accent-red' : 'text-accent-gold'}`}>
                            {rule.level}
                          </div>
                          <p className="mt-1 text-sm text-ink-tertiary leading-relaxed italic">
                            {rule.copy}
                          </p>
                        </div>
                      ))}
                    </div>
                  </article>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      <section id="candidate-board" className="px-6 md:px-14 py-14 bg-bg-deep border-y border-border">
        <SectionHeader
          eyebrow="CANDIDATE BOARD"
          title="Every keeper has a price."
        />

        <div className="space-y-3">
          {candidates.map((candidate) => (
            <CandidateRow key={candidate.player} candidate={candidate} />
          ))}
        </div>
      </section>

      <section className="px-6 md:px-14 py-14">
        <SectionHeader
          eyebrow="EVIDENCE PACK"
          title={isDemo ? 'The unlock should feel researched.' : 'Receipts behind the verdict.'}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {evidencePack.map((pack) => {
            const Icon = ICONS[pack.iconKey] || Search;
            return (
              <article key={pack.title} className="bg-bg-surface border-t-[3px] border-accent-gold p-5">
                <Icon aria-hidden="true" className="w-5 h-5 text-accent-gold mb-4" />
                <div className="font-display text-3xl font-black uppercase tracking-normal leading-none">
                  {pack.title}
                </div>
                <ul className="mt-4 space-y-2">
                  {pack.items.map((item) => (
                    <li key={item} className="flex gap-2 text-sm text-ink-secondary leading-snug italic">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-accent-gold" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </section>

      <section className="px-6 md:px-14 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_0.92fr] gap-6 md:gap-8">
          <article className="bg-bg-surface border-t-[3px] border-accent-gold p-6 md:p-8">
            <SectionHeader
              eyebrow="SCORING TRANSLATION"
              title="Generic rankings are not enough."
              className="mb-6"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {scoringTranslation.map((item) => (
                <div key={item.title} className="bg-bg-deep border border-border p-4">
                  <div className="font-display text-2xl font-black uppercase tracking-normal">
                    {item.title}
                  </div>
                  <p className="mt-2 text-sm text-ink-tertiary leading-relaxed italic">
                    {item.copy}
                  </p>
                </div>
              ))}
            </div>
          </article>

          <article className="bg-bg-surface border-t-[3px] border-accent-red p-6 md:p-8">
            <Eyebrow color="red">WHAT COULD CHANGE IT</Eyebrow>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-black uppercase tracking-normal leading-none">
              The answer should stay honest.
            </h2>
            <div className="mt-6 space-y-3">
              {changeNotes.map((note) => (
                <div key={note} className="flex gap-3 border-b border-border pb-3 last:border-b-0">
                  <AlertTriangle aria-hidden="true" className="w-4 h-4 text-accent-red shrink-0 mt-0.5" />
                  <p className="text-sm text-ink-secondary leading-relaxed italic">
                    {note}
                  </p>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>

      {dataCaveats.length > 0 && (
        <section className="px-6 md:px-14 py-12 bg-bg-deep border-y border-border">
          <SectionHeader
            eyebrow="DATA CAVEATS"
            title="Where the inputs are soft."
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {dataCaveats.map((caveat) => (
              <article key={caveat} className="bg-bg-surface border-l-[3px] border-accent-red p-5">
                <p className="text-sm text-ink-secondary leading-relaxed italic">{caveat}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="px-6 md:px-14 py-14 bg-bg-deep border-y border-border">
        <SectionHeader
          eyebrow="WEEKLY UPDATE CADENCE"
          title="Frozen at the draft."
        />
        <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-6 md:gap-8">
          <article className="bg-bg-surface border-t-[3px] border-accent-gold p-6 md:p-8">
            <div className="flex items-start gap-4">
              <RefreshCw aria-hidden="true" className="w-7 h-7 text-accent-gold shrink-0 mt-1" />
              <div>
                <div className="font-display text-4xl md:text-5xl font-black uppercase tracking-normal leading-none">
                  Update every Tuesday.
                </div>
                <p className="mt-4 text-sm md:text-base text-ink-secondary leading-relaxed italic">
                  From now until the {meta.draftLabel || BRUINS09_LEAGUE_SETTINGS.draftLabel} draft, the dossier should be refreshed weekly.
                  If major player news hits between updates, the affected manager page gets a hotfix.
                </p>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {changeLog.map((entry) => (
                <div key={entry.title} className="bg-bg-deep border border-border p-4">
                  <div className="text-[10px] tracking-[0.25em] text-accent-gold font-extrabold uppercase">
                    {entry.date}
                  </div>
                  <div className="mt-2 font-display text-2xl font-black uppercase tracking-normal leading-none">
                    {entry.title}
                  </div>
                  <p className="mt-2 text-sm text-ink-tertiary leading-relaxed italic">
                    {entry.copy}
                  </p>
                </div>
              ))}
            </div>
          </article>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {weeklyRefresh.map((item) => (
              <article key={item.label} className="bg-bg-surface border border-border p-5">
                <div className="text-[10px] tracking-[0.25em] text-accent-gold font-extrabold uppercase">
                  Refresh rule
                </div>
                <div className="mt-2 font-display text-3xl font-black uppercase tracking-normal leading-none">
                  {item.label}
                </div>
                <p className="mt-3 text-sm text-ink-secondary leading-relaxed italic">
                  {item.copy}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-14 py-14 bg-bg-deep border-t border-border">
        <SectionHeader
          eyebrow="VALIDATION STANDARD"
          title="No source, no verdict."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {sourceRequirements.map(([label, copy, status]) => (
            <article key={label} className="bg-bg-surface border border-border p-5">
              <div className="flex items-start justify-between gap-4">
                <Search aria-hidden="true" className="w-5 h-5 text-accent-gold shrink-0" />
                <span className="text-[9px] tracking-[0.22em] text-accent-gold font-extrabold uppercase">
                  {status}
                </span>
              </div>
              <div className="mt-4 font-display text-3xl font-black uppercase tracking-normal leading-none">
                {label}
              </div>
              <p className="mt-3 text-sm text-ink-secondary leading-relaxed italic">
                {copy}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-14 py-14">
        <SectionHeader
          eyebrow="SOURCE LEDGER"
          title="Show the receipts."
        />
        <div className="bg-bg-surface border border-border overflow-hidden">
            <div className="hidden md:grid grid-cols-[0.7fr_0.7fr_1.6fr_0.5fr] gap-4 bg-bg-deep px-5 py-3 text-[10px] tracking-[0.24em] text-ink-tertiary font-extrabold uppercase">
            <div>Source type</div>
            <div>Used for</div>
            <div>Why it matters</div>
            <div>Cadence</div>
          </div>
          {sourceLedger.map((source) => (
            <div key={source.type} className="grid grid-cols-1 md:grid-cols-[0.7fr_0.7fr_1.6fr_0.5fr] gap-2 md:gap-4 border-t border-border px-5 py-4">
              <LedgerCell label="Source type" value={source.label} strong />
              <LedgerCell label="Used for" value={source.usedFor} />
              <LedgerCell label="Why it matters" value={source.why} />
              <LedgerCell label="Cadence" value={source.cadence} accent />
            </div>
          ))}
        </div>
        <div className="mt-4 border-l-[3px] border-accent-gold bg-bg-surface p-4 text-sm text-ink-secondary leading-relaxed italic">
          Source coverage check: {missingSourceCount === 0
            ? 'all required source types are represented in the data contract.'
            : `${missingSourceCount} required source types are still missing.`}
        </div>
      </section>

      <section className="px-6 md:px-14 py-12">
        <div className="bg-bg-surface border-l-[3px] border-accent-gold p-5 md:p-6">
          <div className="flex items-start gap-4">
            <ClipboardList aria-hidden="true" className="w-6 h-6 text-accent-gold shrink-0 mt-1" />
            <div>
              <div className="font-display text-3xl md:text-4xl font-black uppercase tracking-normal leading-none">
                Builder note
              </div>
              <p className="mt-3 text-sm md:text-base text-ink-secondary leading-relaxed italic">
                {isDemo
                  ? 'This is the target unlocked experience. The Yahoo roster capture and 2025 draft-cost mapping are now local. Next real work is the weekly research workflow that refreshes ADP, projections, rankings, and player news from now through the draft.'
                  : 'This dossier is generated from authenticated Yahoo roster and draft evidence, the commissioner-confirmed no-pick rule, and the dated Aug. 19 market snapshot. It becomes final after the Aug. 25 news and market refresh.'}
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function MiniStatus({ label, value, tone = 'gold' }) {
  const valueClass = tone === 'red' ? 'text-accent-red' : 'text-accent-gold';

  return (
    <div className="bg-bg-surface/60 border border-border p-3">
      <div className="text-[9px] tracking-[0.24em] text-ink-tertiary font-extrabold uppercase">
        {label}
      </div>
      <div className={`mt-1 font-display text-xl font-black uppercase tracking-normal leading-none ${valueClass}`}>
        {value}
      </div>
    </div>
  );
}

function Readout({ label, value }) {
  return (
    <div className="bg-bg-deep border border-border p-4">
      <div className="text-[9px] tracking-[0.25em] text-ink-tertiary font-extrabold uppercase">
        {label}
      </div>
      <div className="mt-2 font-display text-2xl font-black uppercase tracking-normal text-accent-gold">
        {value}
      </div>
    </div>
  );
}

function CandidateRow({ candidate }) {
  const riskClass = candidate.risk === 'High' ? 'text-accent-red' : 'text-accent-gold';
  const hasDetails = Boolean(
    candidate.surplusReasoning || candidate.riskDrivers || candidate.keeperImplication
  );

  return (
    <article className="bg-bg-surface border border-border p-4 md:p-5">
      <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_repeat(5,minmax(95px,0.55fr))] gap-4 lg:items-start">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-[10px] tracking-[0.2em] font-extrabold uppercase text-accent-gold">
            <span>{candidate.position}</span>
            <span className="text-accent-gold/40">/</span>
            <span>{candidate.team}</span>
          </div>
          <div className="mt-1 font-display text-3xl md:text-4xl font-black uppercase tracking-normal leading-none">
            {candidate.player}
          </div>
          <p className="mt-2 text-sm text-ink-secondary leading-relaxed italic">
            {candidate.note}
          </p>
        </div>

        <Metric label="Cost" value={candidate.cost} />
        <Metric label="Market" value={candidate.market} />
        <Metric label="Surplus" value={candidate.surplus} emphasis />
        <Metric label="Grade" value={candidate.grade} />
        <Metric label="Risk" value={candidate.risk} className={riskClass} />
      </div>

      {hasDetails && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3 border-t border-border pt-4">
          {candidate.surplusReasoning && (
            <DetailBlock
              eyebrow="Why this surplus"
              accent="gold"
              copy={candidate.surplusReasoning}
            />
          )}
          {candidate.riskDrivers && (
            <DetailBlock
              eyebrow={`Why "${candidate.risk}" risk`}
              accent={candidate.risk === 'High' ? 'red' : 'gold'}
              copy={candidate.riskDrivers}
            />
          )}
          {candidate.keeperImplication && (
            <DetailBlock
              eyebrow="What this means for Ryan"
              accent="gold"
              copy={candidate.keeperImplication}
            />
          )}
        </div>
      )}
    </article>
  );
}

function DetailBlock({ eyebrow, copy, accent = 'gold' }) {
  const accentClass = accent === 'red' ? 'border-accent-red text-accent-red' : 'border-accent-gold text-accent-gold';
  return (
    <div className={`bg-bg-deep border-l-[3px] p-4 ${accentClass.split(' ')[0]}`}>
      <div className={`text-[10px] tracking-[0.22em] font-extrabold uppercase ${accentClass.split(' ')[1]}`}>
        {eyebrow}
      </div>
      <p className="mt-2 text-sm text-ink-secondary leading-relaxed italic">
        {copy}
      </p>
    </div>
  );
}

function Metric({ label, value, emphasis = false, className = '' }) {
  return (
    <div className="min-w-0">
      <div className="text-[9px] tracking-[0.25em] text-ink-tertiary font-extrabold uppercase">
        {label}
      </div>
      <div className={`mt-1 font-display text-2xl font-black uppercase tracking-normal leading-none break-words ${
        emphasis ? 'text-accent-gold' : className || 'text-ink-primary'
      }`}>
        {value}
      </div>
    </div>
  );
}

function LedgerCell({ label, value, strong = false, accent = false }) {
  return (
    <div className="min-w-0">
      <div className="md:hidden text-[9px] tracking-[0.24em] text-ink-tertiary font-extrabold uppercase">
        {label}
      </div>
      <div className={`mt-1 md:mt-0 text-sm leading-relaxed ${
        strong
          ? 'font-display text-2xl font-black uppercase tracking-normal text-ink-primary leading-none'
          : accent
            ? 'font-display text-xl font-black uppercase tracking-normal text-accent-gold leading-none'
            : 'text-ink-secondary italic'
      }`}>
        {value}
      </div>
    </div>
  );
}
